import React, { useContext, useEffect, useState } from 'react';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { AuthContext } from './../AuthContext';
import api from './../api.js';
import { isStaffUser } from './../role.js';
import { IndianCurrencyFormatter } from './../Function';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import '../Staff/Staff.css';

function monthStart() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${m}-01`;
}

function today() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function payBadge(item) {
  if (String(item.is_paid) === '2') {
    return <span className="badge badge-warning">Cancelled</span>;
  }
  if (item.paytype === 'online') {
    return String(item.is_paid) === '0'
      ? <span className="badge badge-success">Paid (Online)</span>
      : <span className="badge badge-warning">Pending (Online)</span>;
  }
  return String(item.is_paid) === '0'
    ? <span className="badge badge-success">Paid (Cash)</span>
    : <span className="badge badge-danger">Unpaid (Cash)</span>;
}

function Index() {
  const { user, fulluser } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState('all');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [summary, setSummary] = useState(null);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }, [location]);

  useEffect(() => {
    if (isStaffUser(fulluser)) {
      navigate('/StaffReport');
    }
  }, [fulluser]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/vendor/sales-report?from=${from}&to=${to}&staff_id=${staffId || 'all'}`);
      if (response.data.status === 'success') {
        setSummary(response.data.summary || null);
        setBills(response.data.bills || []);
        setStaffList(response.data.staff_list || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) loadReport();
  }, [user, staffId, from, to]);

  const cards = [
    { label: 'Sales amount', value: summary?.sales || 0 },
    { label: 'Collection', value: summary?.collection || 0 },
    { label: 'Cash sales', value: summary?.cash || 0 },
    { label: 'Online sales', value: summary?.online || 0 },
    { label: 'Reward used', value: summary?.reward_used || 0 },
    { label: 'Admin commission', value: summary?.admin_commission || 0 },
    { label: 'Cancel amount', value: summary?.cancel_amount || 0 },
  ];

  return (
    <div id="appCapsule" className="staff-page">
      <Footer />
      <Sidebar />
      <div className="section mt-2">
        <div className="section-heading">
          <h2 className="title">Sales Report</h2>
        </div>
        <div className="card mb-2">
          <div className="card-body">
            <div className="form-group basic">
              <label className="label">Staff</label>
              <select
                className="form-control"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
              >
                <option value="all">All (Vendor + Staff)</option>
                <option value="vendor">Vendor only</option>
                {staffList.map((item) => (
                  <option key={item.id} value={item.id}>{item.name} - {item.phone}</option>
                ))}
              </select>
            </div>
            <div className="row">
              <div className="col-6">
                <label className="label">From</label>
                <input type="date" className="form-control" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="col-6">
                <label className="label">To</label>
                <input type="date" className="form-control" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-3">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : summary ? (
          <>
            <div className="row staff-summary mb-2">
              <div className="col-4">
                <div className="stat-box">
                  <div className="title">Bills</div>
                  <div className="value">{summary.bills_count || 0}</div>
                </div>
              </div>
              <div className="col-4">
                <div className="stat-box">
                  <div className="title">Paid</div>
                  <div className="value text-success">{summary.paid_count || 0}</div>
                </div>
              </div>
              <div className="col-4">
                <div className="stat-box">
                  <div className="title">Cancelled</div>
                  <div className="value text-danger">{summary.cancel_count || 0}</div>
                </div>
              </div>
            </div>
            <div className="row staff-summary">
              {cards.map((card) => (
                <div className="col-6 mb-2" key={card.label}>
                  <div className="stat-box">
                    <div className="title">{card.label}</div>
                    <div className="value text-primary" style={{ fontSize: '16px' }}>
                      <IndianCurrencyFormatter amount={card.value} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>

      <div className="section mb-5">
        <div className="section-heading">
          <h2 className="title">Bill details</h2>
        </div>
        {bills.length === 0 ? (
          <div className="card">
            <div className="card-body text-center text-muted">No bills in this date range.</div>
          </div>
        ) : (
          <ul className="listview image-listview">
            {bills.map((item) => (
              <li key={item.id}>
                <Link to={`/Payment?id=${item.id}`} className="item">
                  <div className="in" style={{ width: '100%' }}>
                    <div>
                      <strong>#{item.billno}</strong>
                      <div className="text-muted small">{item.date_time}</div>
                      <div className="small">{item.created_by}</div>
                      <div className="small">{item.customer_name || 'Customer'} {item.customer_phone ? `- ${item.customer_phone}` : ''}</div>
                      <div className="mt-1">{payBadge(item)}</div>
                    </div>
                    <div className="text-end">
                      <div className="price text-danger">
                        <IndianCurrencyFormatter amount={(parseFloat(item.billamount || 0) + parseFloat(item.tax || 0))} />
                      </div>
                      {parseFloat(item.customer_reward_used || 0) > 0 && (
                        <div className="small text-success">Reward <IndianCurrencyFormatter amount={item.customer_reward_used} /></div>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default Index;
